import {
    useContext,
    useEffect,
    Fragment,
    useMemo,
    type FC
} from "react";
import {
    AccessibilityInfo,
    Platform,
    View
} from "react-native";
import type IAlertProps from "./type";
import type {
    AlertContextType,
    AlertType
} from "./type";
import stylesheet, {
    ALERT_ACTION_CONTAINER_COLORS,
    ALERT_ACTION_VARIANTS,
    getAlertActionRadius,
    getAlertCloseRadius,
    getAlertLiveRegion,
    ALERT_MAX_ACTIONS,
    getAlertColors,
    getAlertRole,
    useStyles
} from "./stylesheet";
import AlertActions from "./components/actions";
import AlertDescription from "./components/description";
import AlertTitle from "./components/title";
import {
    AlertContext
} from "./context";
import {
    getHasAlertDescription,
    getAlertAnnouncement
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    type NCoreUIKitIcon
} from "../../types";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    TriangleAlert as TriangleAlertIcon,
    CircleCheck as CircleCheckIcon,
    CircleAlert as CircleAlertIcon,
    Sparkles as SparklesIcon,
    type LucideIcon,
    Info as InfoIcon,
    Bell as BellIcon,
    X as XIcon
} from "lucide-react-native";
import {
    warnOnce
} from "../../utils/development";
import {
    CardContext
} from "../card/context";
import AnimatedIcon from "../animatedIcon";
import Button from "../button";

const AlertTypeIcon: Record<AlertType, LucideIcon> = {
    warning: TriangleAlertIcon,
    success: CircleCheckIcon,
    danger: CircleAlertIcon,
    primary: SparklesIcon,
    neutral: BellIcon,
    info: InfoIcon
};

const AlertCloseIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => <AnimatedIcon
    customColor={customColor}
    animationTrigger="hover"
    animation="signature"
    color={color}
    icon={XIcon}
    size={size}
/>;

const Alert: FC<IAlertProps> = ({
    actionPlacement = "bottom",
    "aria-live": ariaLiveProp,
    isClosable = false,
    announceBehaviour,
    variant = "tonal",
    isBanner = false,
    type = "neutral",
    customLocalize,
    icon: IconProp,
    role: roleProp,
    customTheme,
    description,
    children,
    actions,
    onClose,
    style,
    title,
    ...props
}) => {
    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        shadowAlpha,
        elevations,
        typography,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const cardContext = useContext(CardContext);

    const isCompact = !getHasAlertDescription({
        description,
        children
    });

    const currentColors = useMemo(() => getAlertColors({
        variant,
        colors,
        type
    }), [
        variant,
        colors,
        type
    ]);

    const currentRole = getAlertRole({
        announceBehaviour,
        role: roleProp,
        type
    });

    const currentActions = (actions ?? []).slice(0, ALERT_MAX_ACTIONS);

    const closeRadius = getAlertCloseRadius({
        controlHeights,
        typography,
        radiuses,
        borders,
        spaces
    });

    const actionRadius = getAlertActionRadius({
        controlHeights,
        radiuses,
        borders
    });

    const {
        container: containerDynamicStyle,
        close: closeDynamicStyle,
        icon: iconDynamicStyle,
        body: bodyDynamicStyle
    } = useStyles({
        isBanner: isBanner || variant === "banner",
        cardInset: cardContext?.inset,
        controlPaddings,
        controlHeights,
        currentColors,
        shapeConfigs,
        touchTargets,
        pointerType,
        shadowAlpha,
        elevations,
        typography,
        iconSizes,
        isCompact,
        radiuses,
        borders,
        variant,
        colors,
        spaces
    });

    const isActionAtEnd = actionPlacement === "end" && !isCompact && currentActions.length > 0;

    const context = useMemo<AlertContextType>(() => ({
        isActionInline: isActionAtEnd,
        currentColors,
        customTheme,
        isCompact
    }), [
        isActionAtEnd,
        currentColors,
        customTheme,
        isCompact
    ]);

    const currentLiveRegion = getAlertLiveRegion({
        ariaLive: ariaLiveProp,
        role: currentRole
    });

    useEffect(() => {
        if(Platform.OS !== "ios" || !announceBehaviour || announceBehaviour === "none") {
            return;
        }

        AccessibilityInfo.announceForAccessibility(getAlertAnnouncement({
            description,
            title
        }));
    }, []);

    useEffect(() => {
        if(title || children) {
            return;
        }

        warnOnce({
            message: "Alert: an alert needs a \"title\" (or Alert.Title children) so it can say what happened."
        });
    }, [
        children,
        title
    ]);

    useEffect(() => {
        if(!isClosable || onClose) {
            return;
        }

        warnOnce({
            message: "Alert: \"isClosable\" needs an \"onClose\" handler that removes the alert."
        });
    }, [
        isClosable,
        onClose
    ]);

    useEffect(() => {
        if((actions?.length ?? 0) <= ALERT_MAX_ACTIONS) {
            return;
        }

        warnOnce({
            message: `Alert: only the first ${ALERT_MAX_ACTIONS} actions are rendered.`
        });
    }, [
        actions?.length
    ]);

    const handleClosePress = () => {
        onClose?.();
    };

    const renderIcon = () => {
        const TypeIcon = AlertTypeIcon[type];

        return <View
            style={[
                stylesheet.icon,
                iconDynamicStyle
            ]}
            aria-hidden={true}
        >
            {
                IconProp ?
                    <IconProp
                        customColor={currentColors.iconColor}
                        color={currentColors.iconColorKey}
                        size={iconSizes.small}
                    />
                :
                    <TypeIcon
                        color={currentColors.iconColor}
                        size={iconSizes.small}
                    />
            }
        </View>;
    };

    const renderActions = () => {
        if(!currentActions.length) {
            return null;
        }

        return <AlertActions>
            {
                currentActions.map((action, index) => <Button
                    {...action}
                    borderRadius={action.borderRadius ?? (isCompact ? actionRadius : undefined)}
                    customColor={action.customColor ?? ALERT_ACTION_CONTAINER_COLORS[index]}
                    customTheme={action.customTheme ?? customTheme}
                    variant={ALERT_ACTION_VARIANTS[index]}
                    spreadBehaviour="free"
                    size="small"
                    type={type}
                    key={index}
                />)
            }
        </AlertActions>;
    };

    const renderContent = () => {
        if(isActionAtEnd) {
            return <View
                style={[
                    stylesheet.endRow,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.endText,
                        {
                            gap: spaces.spacingXxs
                        }
                    ]}
                >
                    {
                        title ?
                            <AlertTitle>
                                {title}
                            </AlertTitle>
                        :
                            null
                    }
                    <AlertDescription>
                        {description}
                    </AlertDescription>
                </View>
                {renderActions()}
            </View>;
        }

        return <Fragment>
            {
                title ?
                    <AlertTitle>
                        {title}
                    </AlertTitle>
                :
                    null
            }
            {
                isCompact ?
                    null
                :
                    <AlertDescription>
                        {description}
                    </AlertDescription>
            }
            {renderActions()}
        </Fragment>;
    };

    const renderClose = () => {
        if(!isClosable) {
            return null;
        }

        return <Button
            accessibilityLabel={localize("close-alert")}
            onPress={handleClosePress}
            borderRadius={closeRadius}
            customTheme={customTheme}
            spreadBehaviour="free"
            style={[
                stylesheet.close,
                closeDynamicStyle
            ]}
            icon={AlertCloseIcon}
            variant="ghost"
            size="small"
            type={type}
        />;
    };

    return <AlertContext.Provider
        value={context}
    >
        <View
            {...props}
            aria-live={currentLiveRegion}
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle
            ]}
            role={currentRole}
        >
            {renderIcon()}
            <View
                style={[
                    stylesheet.body,
                    bodyDynamicStyle
                ]}
            >
                {children ?? renderContent()}
            </View>
            {renderClose()}
        </View>
    </AlertContext.Provider>;
};
export default Object.assign(Alert, {
    Description: AlertDescription,
    Actions: AlertActions,
    Title: AlertTitle
});
